import type { UserSubscriptionStateRow } from "@/types/userSubscriptionState";

const DAY_MS = 86_400_000;
const CANCEL_WINDOW_DAYS = 14;

const CANCEL_STATUSES = new Set(["pending-cancel", "cancelled", "canceled"]);

export type SubscriptionUiModel = {
  /** Payment / card under-header message */
  cardMessage: "expiring" | "expired" | "declined" | null;
  /** Cancelled / pending-cancel with access ending within 14 days */
  showCancelCountdown: boolean;
  accessEndsAt: string | null;
  nextPaymentAt: string | null;
  cardExpiresAt: string | null;
  /** Prefer for Update Payment Method CTA when present */
  paymentUpdateUrl: string | null;
};

/**
 * Derive card / cancel banner flags from `user_subscription_state`.
 *
 * Expiring-card: only when status is `active` and there is a renewal
 * (`next_payment_at`). No renewal → nothing to warn about; pending-cancel
 * uses `access_ends_at` instead.
 */
export function deriveSubscriptionUi(
  row: UserSubscriptionStateRow | null,
): SubscriptionUiModel {
  let cardMessage: SubscriptionUiModel["cardMessage"] = null;
  if (row) {
    const declined =
      row.subscription_status === "on-hold" ||
      Boolean(row.last_payment_failed_at);
    if (declined) {
      cardMessage = "declined";
    } else if (
      row.subscription_status === "active" &&
      row.next_payment_at &&
      row.card_status === "expiring"
    ) {
      cardMessage = "expiring";
    } else if (row.card_status === "expired") {
      cardMessage = "expired";
    }
  }

  let showCancelCountdown = false;
  const accessEndsAt = row?.access_ends_at ?? null;
  const status = row?.subscription_status ?? "";
  if (CANCEL_STATUSES.has(status) && accessEndsAt) {
    const ends = new Date(accessEndsAt).getTime();
    if (!Number.isNaN(ends)) {
      const remaining = ends - Date.now();
      showCancelCountdown =
        remaining > 0 && remaining <= CANCEL_WINDOW_DAYS * DAY_MS;
    }
  }

  return {
    cardMessage,
    showCancelCountdown,
    accessEndsAt,
    nextPaymentAt: row?.next_payment_at ?? null,
    cardExpiresAt: row?.card_expires_at ?? null,
    paymentUpdateUrl: row?.payment_update_url ?? null,
  };
}
